import { useCallback, useLayoutEffect, useRef, useState } from "react";

import type { Gesture, Point, Size } from "../types.ts";
import { useDimensions } from "./DimensionsContext";
import { PanContext } from "./PanContext";
import { useZoom } from "./ZoomContext";

const getBounds = (width: number, height: number, viewportSize: Size | undefined, zoom: number) => {
  const panWidth = width - (viewportSize?.width ?? 0) / zoom;
  const panHeight = height - (viewportSize?.height ?? 0) / zoom;

  return {
    left: panWidth / -2,
    right: panWidth / 2,
    top: panHeight / -2,
    bottom: panHeight / 2,
  };
};

const adjustPanToNewZoom = (previousZoom: number, newZoom: number, origin: Point, pan: Point): Point => {
  const originGravity = (newZoom - previousZoom) / (newZoom * previousZoom);
  return {
    x: pan.x - origin.x * originGravity,
    y: pan.y - origin.y * originGravity,
  };
};

const usePanState = (
  initialValue: Point,
): [
  PanContext["pan"],
  PanContext["scaledPan"],
  PanContext["scaledBounds"],
  PanContext["onPanDrag"],
  PanContext["onPanReset"],
  PanContext["onPanGesture"],
] => {
  const { width, height, viewportSize } = useDimensions();
  const { zoom, zoomOrigin } = useZoom();
  const [pan, setPan] = useState(initialValue);

  /*
    Pan values can be updated in three different ways:
      1. When PerspectiveTarget `onPanDrag` callback is called (= when <Draggable> `onDrag` is called) 
      2. When the zoom value is updated, in order to keep the pointer position on the same element
      3. When a wheel or pinch gesture is used (= when `GestureHandler` calls `onPanGesture`)

    Gestures properties (like `scale` and `translation`) are relative to their "origin" position,
    which is defined at the beginning of the gesture. Hence the need to keep `atRest` mutable values.
  */

  const [isGesturing, setIsGesturing] = useState(false);
  const zoomAtRest = useRef(zoom);
  const panAtRest = useRef(initialValue);

  const setBoundedPan = useCallback(
    (desiredPan: Point): Point => {
      const bounds = getBounds(width, height, viewportSize, zoom);
      const boundedPan = {
        x: Math.min(Math.max(bounds.left, desiredPan.x), bounds.right),
        y: Math.min(Math.max(bounds.top, desiredPan.y), bounds.bottom),
      };
      setPan(boundedPan);
      return boundedPan;
    },
    [height, viewportSize, width, zoom],
  );

  const onPanDrag = useCallback(
    (position: Point) => {
      const unscaledPan = {
        x: position.x / zoom,
        y: position.y / zoom,
      };
      panAtRest.current = setBoundedPan(unscaledPan);
    },
    [setBoundedPan, zoom],
  );

  const onPanReset = useCallback(() => {
    setPan({ x: 0, y: 0 });
    panAtRest.current = { x: 0, y: 0 };
  }, []);

  useLayoutEffect(() => {
    if (isGesturing || zoomAtRest.current === zoom) return;
    panAtRest.current = setBoundedPan(adjustPanToNewZoom(zoomAtRest.current, zoom, zoomOrigin, panAtRest.current));
    zoomAtRest.current = zoom;
  }, [isGesturing, zoomAtRest, setBoundedPan, zoom, zoomOrigin]);

  const onPanGesture = useCallback(
    (gesture?: Gesture) => {
      if (gesture) {
        setIsGesturing(true);
        const translatedPan = {
          x: panAtRest.current.x + gesture.translation.x / zoom,
          y: panAtRest.current.y + gesture.translation.y / zoom,
        };
        setBoundedPan(adjustPanToNewZoom(zoomAtRest.current, zoom, zoomOrigin, translatedPan));
      } else {
        zoomAtRest.current = zoom;
        panAtRest.current = pan;
        setIsGesturing(false);
      }
    },
    [pan, setBoundedPan, zoom, zoomOrigin],
  );

  const scaledPan = {
    x: pan.x * zoom,
    y: pan.y * zoom,
  };

  const bounds = getBounds(width, height, viewportSize, zoom);

  const scaledBounds = {
    left: bounds.left * zoom,
    right: bounds.right * zoom,
    top: bounds.top * zoom,
    bottom: bounds.bottom * zoom,
  };

  return [pan, scaledPan, scaledBounds, onPanDrag, onPanReset, onPanGesture];
};

export const PanProvider = ({ initialValue, children }: { initialValue: Point; children: React.ReactNode }) => {
  const [pan, scaledPan, scaledBounds, onPanDrag, onPanReset, onPanGesture] = usePanState(initialValue);
  return (
    <PanContext.Provider value={{ pan, scaledPan, scaledBounds, onPanDrag, onPanReset, onPanGesture }}>
      {children}
    </PanContext.Provider>
  );
};
